import React from 'react';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

import MobileMenuCloseIcon from 'assets/images/menu/mobile/mb_close_btn.svg';
import MobileHamburgerMenuIcon from 'assets/images/menu/mobile/mb_hamburger_btn.svg';
import SNSLoginModal from 'contents/modal/SNSLoginModal';
import { useRecoilValue } from 'recoil';
import { userSNSState } from 'recoils/user/userState';
import styled, { css } from 'styled-components';

import Button from 'components/button/Button';
import Logo from 'components/logo/Logo';
import {
  HeaderContainer,
  HeaderInner,
  HeaderWrapper,
} from 'components/templates/header/web/Header';

import useGetLocal from 'hooks/common/useGetLocal';
import useViewMode from 'hooks/common/useViewMode';
import useHeader from 'hooks/menu/useHeader';
import { useHeaderMobile } from 'hooks/menu/useHeaderMobile';
import { useMenuBarMobile } from 'hooks/menu/useMenuBarMobile';
import useMenuCommon from 'hooks/menu/useMenuCommon';

import { TokenManager } from 'utils/security/securityUtil';
import { clickToGTM } from 'utils/seo/seoGTMUtil';

import { MenuBarMobile } from './MenuBarMobile';

const HeaderMobile = () => {
  const mediaQuery = useMediaQuery({ query: '(max-width: 768px)' });
  const userSnsInfo = useRecoilValue(userSNSState);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const viewMode = searchParams.get('view');
  const { isToggleMenu, onVisibleMobileMenu, setIsToggleMenu } =
    useHeaderMobile();
  const { isSlideMenu, onHandleSubMenuToggle, onHandleSlideReset } =
    useMenuBarMobile();
  useGetLocal();
  useMenuCommon();

  const { isMobile } = useViewMode();
  const { onLoginBtnClick, onLogoutBtnClick } = useHeader();

  if (mediaQuery && viewMode === null) {
    const separator = location.search ? '&' : '?';
    return (
      <Navigate
        to={`${location.pathname}${location.search}${separator}view=mobile`}
      />
    );
  }
  return (
    <>
      {/* 헤더 영역 */}
      <HeaderWrapper $isToggleMenu={isToggleMenu} className="header">
        <HeaderInnerMobile
          $isToggleMenu={isToggleMenu}
          className="header_inner"
          $isMobile={isMobile}
        >
          <HeaderContainerMobile className="header_container">
            <Logo
              isMobile={true}
              isToggleMenu={isToggleMenu}
              onHandleSlideReset={onHandleSlideReset}
              setIsToggleMenu={setIsToggleMenu}
              linkTo="/?view=mobile"
            />

            <HeaderButtonGroup>
              {/* 로그인/로그아웃 버튼 */}
              {!localStorage.getItem(TokenManager.token.access) ? (
                <LoginLogoutButton
                  variant="primary"
                  size="small"
                  shape="round20"
                  onClick={() => {
                    setIsToggleMenu(false);
                    onLoginBtnClick();
                  }}
                  gtm={{
                    id: 'header-login-btn',
                    target: '로그인',
                    url: '/login',
                  }}
                >
                  로그인
                </LoginLogoutButton>
              ) : (
                <LoginLogoutButton
                  variant="primary"
                  size="small"
                  shape="round20"
                  gtm={{
                    id: 'header-logout',
                    target: '로그아웃',
                    url: '/',
                  }}
                  onClick={() => {
                    setIsToggleMenu(false);
                    onLogoutBtnClick();
                  }}
                >
                  로그아웃
                </LoginLogoutButton>
              )}

              {/* 햄버거 메뉴 버튼 */}
              <Button onClick={onVisibleMobileMenu}>
                {isToggleMenu ? (
                  <img
                    src={MobileMenuCloseIcon}
                    alt="menu close"
                    style={{ marginRight: '4px' }}
                  />
                ) : (
                  <img src={MobileHamburgerMenuIcon} alt="menu show" />
                )}
              </Button>
            </HeaderButtonGroup>
            {/* 메뉴바 */}
            <MenuBarMobile
              isToggleMenu={isToggleMenu}
              setIsToggleMenu={setIsToggleMenu}
              isSlideMenu={isSlideMenu}
              onHandleSubMenuToggle={onHandleSubMenuToggle}
            />
          </HeaderContainerMobile>
        </HeaderInnerMobile>
      </HeaderWrapper>
      {/* 회원가입 모달 */}
      {userSnsInfo.isopen && <SNSLoginModal />}
    </>
  );
};

export default HeaderMobile;

const HeaderInnerMobile = styled(HeaderInner)`
  padding: 0 16px;
  ${({ $isToggleMenu }) =>
    $isToggleMenu
      ? css`
          border-bottom: 1px solid ${({ theme }) => theme.color.gray200};
        `
      : css`
          border-bottom: 0;
        `};
  max-width: ${({ $isMobile }) => (!$isMobile ? '100%' : '1280px')};
`;
const HeaderContainerMobile = styled(HeaderContainer)`
  width: 100%;
  button {
    margin-left: auto;
  }
`;

const HeaderButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  .login {
    height: 32px;
    padding: 0 12px;
  }
`;

const LoginLogoutButton = styled(Button)`
  padding: 0 12px;
  height: 32px;
`;
